import { usePageTitle } from '../hooks/usePageTitle';
import { useState } from 'react';

const SpinnersPage = () => {
    usePageTitle('Spinners | Quinexa');

    // --- CREDITS INITIALIZATION ---
    const getInitialCredits = () => {
        const storedCredits = localStorage.getItem('QuinexaCredits');
        if (storedCredits === null) {
            localStorage.setItem('QuinexaCredits', '1000');
            return 1000;
        }
        return parseInt(storedCredits, 10);
    };

    // --- STATES ---
    const [credits, setCredits] = useState(getInitialCredits());
    const [user, setUser] = useState(JSON.parse(localStorage.getItem('currentUser')) || { name: 'Guest' });
    const [bet, setBet] = useState(50);
    const [reels, setReels] = useState(['?', '?', '?']);
    const [spinning, setSpinning] = useState([false, false, false]); 
    const [isPlaying, setIsPlaying] = useState(false);
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState(JSON.parse(localStorage.getItem('gameHistory')) || []);

    const symbols = ['7', '$', '*', '#', '@', '%'];

    // --- LOGIC ---
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const handleSpin = async () => {
        if (bet < 10) return alert('Minimum bet is 10 credits');
        if (credits < bet) return alert('Not enough credits');

        setIsPlaying(true);
        setResult(null);

        setSpinning([true, true, true]);

        const currentCredits = credits - bet;
        updateGlobalCredits(currentCredits);

        const currentResults = [...reels];

        for (let i = 0; i < 3; i++) {
            await sleep(500 + i * 400);

            const randomSymbol = symbols[Math.floor(Math.random() * symbols.length)];
            currentResults[i] = randomSymbol;

            setReels([...currentResults]);
            setSpinning(prev => {
                const newState = [...prev];
                newState[i] = false;
                return newState;
            });
        }

        let won = false;
        let multiplier = 0;

        if (currentResults[0] === currentResults[1] && currentResults[1] === currentResults[2]) {
            won = true;
            if (currentResults[0] === '7') multiplier = 10;
            else if (currentResults[0] === '$') multiplier = 8;
            else multiplier = 5;
        } else if (currentResults[0] === currentResults[1] || currentResults[1] === currentResults[2] || currentResults[0] === currentResults[2]) {
            won = true;
            multiplier = 2;
        }

        const winAmount = won ? bet * multiplier : 0;
        const finalCredits = currentCredits + winAmount;

        setResult({ won, amount: won ? winAmount : bet, isResultWon: won, isJackpot: multiplier >= 5 });
        
        updateGlobalCredits(finalCredits);
        addHistory('Spinners', bet, currentResults.join(' '), won);

        setIsPlaying(false);
    };

    const updateGlobalCredits = (newAmount) => {
        setCredits(newAmount);
        localStorage.setItem('QuinexaCredits', newAmount.toString());

        const currentUser = JSON.parse(localStorage.getItem('currentUser'));
        if (currentUser) {
            currentUser.credits = newAmount;
            localStorage.setItem('currentUser', JSON.stringify(currentUser));
            setUser(currentUser); 
        }
    };

    const addHistory = (game, bet, res, won) => {
        const newEntry = { id: Date.now(), game, bet, result: res, won, date: new Date().toLocaleTimeString() };
        const updatedHistory = [newEntry, ...history].slice(0, 5);
        setHistory(updatedHistory);
        localStorage.setItem('gameHistory', JSON.stringify(updatedHistory));
    };

    return (
        <main className="main">
            <div className="container">
                <section className="game-page">
                    <div className="game-container">
                        <div className="game-main">
                            <div className="game-header">
                                <div className="game-header-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                        <rect x="2" y="6" width="6" height="12" rx="1" />
                                        <rect x="9" y="6" width="6" height="12" rx="1" />
                                        <rect x="16" y="6" width="6" height="12" rx="1" />
                                    </svg>
                                </div>
                                <h1 className="game-header-title">Spinners</h1>
                            </div>

                            <div className="game-area">
                                <div className="slot-container">
                                    {reels.map((symbol, index) => (
                                        <div
                                            key={index}
                                            className={`slot-reel ${spinning[index] ? 'spinning' : ''}`}
                                        >
                                            {symbol}
                                        </div>
                                    ))}
                                </div>

                                <div className="game-controls">
                                    <div className="bet-input">
                                        <label>Bet:</label>
                                        <input
                                            type="number"
                                            value={bet}
                                            onChange={(e) => setBet(parseInt(e.target.value) || 0)}
                                            disabled={isPlaying}
                                            min="10"
                                        />
                                    </div>
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleSpin}
                                        disabled={isPlaying}
                                    >
                                        {isPlaying ? 'Spinning...' : 'Spin Reels'}
                                    </button>
                                </div>

                                <div className="game-result">
                                    {result && (
                                        <>
                                            <div className="result-label">
                                                {result.won ? (result.isJackpot ? 'JACKPOT!' : 'YOU WON!') : 'TRY AGAIN'}
                                            </div>
                                            <div className={`result-value ${result.won ? 'win' : 'lose'}`}>
                                                {result.won ? `+${result.amount}` : `-${result.amount}`}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="panel mt-20">
                                <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-medium)' }}>
                                    <strong>How to play:</strong> Set your bet and spin the reels! Match symbols to win.
                                    Symbols: 7, $, *, #, @, %
                                </p>
                            </div>
                        </div>

                        <div className="game-sidebar">
                            <div className="sidebar-card">
                                <h3>Your Credits</h3>
                                <div className="credits-display">{credits.toLocaleString()}</div>
                            </div>

                            <div className="sidebar-card">
                                <h3>Recent Plays</h3>
                                <div className="history-list">
                                    {history.length === 0 ? (
                                        <p style={{ color: 'var(--text-light)', fontSize: '13px' }}>No plays yet</p>
                                    ) : (
                                        history.map(item => (
                                            <div key={item.id} className="history-item">
                                                <span>{item.date}</span> | <span>{item.result}</span> |
                                                <span className={item.won ? 'text-win' : 'text-lose'}> {item.won ? 'Win' : 'Loss'}</span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div className="sidebar-card">
                                <h3>Win Multipliers</h3>
                                <p style={{ fontSize: '13px', color: 'var(--text-medium)', lineHeight: '1.8' }}>
                                    Two Match: <strong style={{ color: 'var(--primary)' }}>2x</strong><br />
                                    Three Match: <strong style={{ color: 'var(--primary)' }}>5x</strong><br />
                                    Three $$$: <strong style={{ color: 'var(--primary)' }}>8x</strong><br />
                                    Three 777: <strong style={{ color: 'var(--primary)' }}>10x</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </main>
    );
};

export default SpinnersPage;